import { Box, Radio, styled, Typography } from '@mui/joy';
import { Control, FieldError, FieldErrors, FieldValues } from 'react-hook-form';
import { Trans } from 'react-i18next';

import { NumberInput } from 'core/components';
import { PricingHelper } from 'pricing/helpers';
import { ICustomerQuotePart } from 'quotes/types';
import { Color, Level } from 'theme/enums';

interface IProps {
  selectedQuotePart: ICustomerQuotePart;
  price: number;
  cost: number;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  choice: { value: number; label: string };
  customMargin: number;
  isSingleSelection?: boolean;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const LabelWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '21px',
});

const RadioChoice = ({
  selectedQuotePart,
  price,
  cost,
  control,
  errors,
  choice,
  customMargin,
  isSingleSelection = false,
  onChange,
  disabled = false,
}: IProps) => {
  const isCustomChoice = -1 === choice.value;

  const priceValue = PricingHelper.formatPricing({
    amount: isCustomChoice
      ? price
      : PricingHelper.computePrice(
          cost,
          isCustomChoice ? customMargin / 100 || 0 : choice.value,
          false
        ),
    decimals: selectedQuotePart?.quotePriceDecimals,
    displayZero: false,
  });

  const priceElement = (
    <Trans
      i18nKey="labelPriceValue"
      values={{ value: priceValue }}
      components={{
        wrapper: <Typography level={Level.TITLE_SM} sx={{ gap: '4px' }}></Typography>,
        labelWrapper: <Typography color={Color.NEUTRAL}></Typography>,
        valueWrapper: <Typography fontWeight={600}></Typography>,
      }}
    />
  );

  const labelElement = (
    <LabelWrapper>
      {choice.label}

      {isSingleSelection && !isCustomChoice && priceElement}
    </LabelWrapper>
  );

  return (
    <LabelWrapper>
      <Radio
        value={choice.value}
        label={labelElement}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
      />

      {isCustomChoice && (
        <>
          <NumberInput
            name="customMargin"
            control={control}
            error={errors?.customMargin as unknown as FieldError}
            onChange={(e) => {
              if (e.source !== 'event' || !onChange) {
                return;
              }

              onChange(Number(e.target.value) / 100);
            }}
            slotProps={{
              input: {
                max: 100,
                decimalScale: 2,
                allowNegative: true,
                suffix: '%',
              },
            }}
            maxWidth="100px"
            disabled={disabled}
          />

          {isSingleSelection && priceElement}
        </>
      )}
    </LabelWrapper>
  );
};

export default RadioChoice;
